import { useCallback } from 'react';
import { toast } from 'sonner';

const buildHighlightContextString = (selectedTextPreserved, contextBefore, contextAfter) => {
  const MAX_SECTION_NAME_LENGTH = 200;
  const SEPARATOR_LENGTH = 2;
  const availableLength = MAX_SECTION_NAME_LENGTH - SEPARATOR_LENGTH;

  let truncatedText = selectedTextPreserved;
  const maxTextLength = Math.min(150, availableLength - 20);
  if (truncatedText.length > maxTextLength) {
    truncatedText = truncatedText.slice(0, maxTextLength - 3) + '...';
  }

  const remainingLength = Math.max(0, availableLength - truncatedText.length);
  const maxContextLength = Math.floor(remainingLength / 2);

  let truncatedBefore = contextBefore;
  if (truncatedBefore.length > maxContextLength) {
    truncatedBefore = '...' + truncatedBefore.slice(-(maxContextLength - 3));
  }

  let truncatedAfter = contextAfter;
  if (truncatedAfter.length > maxContextLength) {
    truncatedAfter = truncatedAfter.slice(0, maxContextLength - 3) + '...';
  }

  let contextString = `${truncatedBefore}|${truncatedText}|${truncatedAfter}`;

  if (contextString.length > MAX_SECTION_NAME_LENGTH) {
    const excess = contextString.length - MAX_SECTION_NAME_LENGTH;
    truncatedAfter = truncatedAfter.slice(0, Math.max(0, truncatedAfter.length - excess));
    contextString = `${truncatedBefore}|${truncatedText}|${truncatedAfter}`;

    if (contextString.length > MAX_SECTION_NAME_LENGTH) {
      const finalExcess = contextString.length - MAX_SECTION_NAME_LENGTH;
      truncatedBefore = truncatedBefore.slice(Math.max(0, finalExcess));
      contextString = `${truncatedBefore}|${truncatedText}|${truncatedAfter}`;
    }

    if (contextString.length > MAX_SECTION_NAME_LENGTH) {
      contextString = contextString.substring(0, MAX_SECTION_NAME_LENGTH);
    }
  }

  return contextString;
};

const extractHighlightContext = (range) => {
  let contextBefore = '';
  let contextAfter = '';

  try {
    const textLayer = range.commonAncestorContainer.closest?.('.react-pdf__Page__textContent');
    if (textLayer) {
      const walker = document.createTreeWalker(
        textLayer,
        NodeFilter.SHOW_TEXT,
        null
      );

      let charCount = 0;
      let startPosition = -1;
      let endPosition = -1;
      let node;

      while ((node = walker.nextNode())) {
        if (node === range.startContainer) {
          startPosition = charCount + range.startOffset;
          break;
        }
        charCount += node.textContent.length;
      }

      walker.currentNode = textLayer;
      charCount = 0;
      while ((node = walker.nextNode())) {
        if (node === range.endContainer) {
          endPosition = charCount + range.endOffset;
          break;
        }
        charCount += node.textContent.length;
      }

      if (startPosition !== -1 && endPosition !== -1) {
        const fullText = textLayer.textContent || '';

        const beforeStart = Math.max(0, startPosition - 50);
        contextBefore = fullText.slice(beforeStart, startPosition).trim();

        const afterEnd = Math.min(fullText.length, endPosition + 50);
        contextAfter = fullText.slice(endPosition, afterEnd).trim();
      } else {
        const fullText = textLayer.textContent || '';
        const selectedTextFromRange = range.toString();
        const firstOccurrenceIndex = fullText.indexOf(selectedTextFromRange);

        if (firstOccurrenceIndex !== -1) {
          const beforeStart = Math.max(0, firstOccurrenceIndex - 50);
          contextBefore = fullText.slice(beforeStart, firstOccurrenceIndex).trim();
          const afterEnd = firstOccurrenceIndex + selectedTextFromRange.length + 50;
          contextAfter = fullText.slice(firstOccurrenceIndex + selectedTextFromRange.length, afterEnd).trim();
        }
      }
    }
  } catch (e) {
    console.warn('Failed to extract context for highlight:', e);
  }

  return { contextBefore, contextAfter };
};

export default function usePdfTextActions({
  selectedText,
  selectionAnchor,
  onAskBudleeAI,
  clearTextSelection,
  isReading,
  setIsReading,
  isReadingRef,
  addHighlight,
  currentPage,
}) {
  const handleAskBudleeAIClick = useCallback((e) => {
    e?.stopPropagation();
    if (selectedText && onAskBudleeAI) {
      onAskBudleeAI(selectedText, null, selectionAnchor);
      clearTextSelection('askBudlee');
    }
  }, [selectedText, onAskBudleeAI, selectionAnchor, clearTextSelection]);

  const handleExplainClick = useCallback((e) => {
    e?.stopPropagation();
    if (selectedText && onAskBudleeAI) {
      onAskBudleeAI(`Explain: "${selectedText}"`, null, selectionAnchor);
      clearTextSelection('explain');
    }
  }, [selectedText, onAskBudleeAI, selectionAnchor, clearTextSelection]);

  const handleReadAloudClick = useCallback((e) => {
    e?.stopPropagation();

    if (isReading) {
      window.speechSynthesis.cancel();
      isReadingRef.current = false;
      setIsReading(false);
      toast.success('Stopped reading', { duration: 2000 });
      return;
    }

    if (!selectedText) return;

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();

      isReadingRef.current = true;
      setIsReading(true);
      toast.success('Reading aloud...', { duration: 2000 });

      const utterance = new SpeechSynthesisUtterance(selectedText);
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      const voices = window.speechSynthesis.getVoices();
      const englishVoice = voices.find(voice => voice.lang.startsWith('en'));
      if (englishVoice) {
        utterance.voice = englishVoice;
      }

      utterance.onend = () => {
        isReadingRef.current = false;
        setIsReading(false);
      };

      utterance.onerror = () => {
        isReadingRef.current = false;
        setIsReading(false);
        toast.error('Failed to read text aloud');
      };

      window.speechSynthesis.speak(utterance);
    } else {
      toast.error('Text-to-speech is not supported in this browser');
    }
  }, [isReading, selectedText, setIsReading, isReadingRef]);

  const handleHighlightClick = useCallback(async () => {
    const textWithContent = selectedText.replace(/^\s+|\s+$/g, '');
    if (!(textWithContent && textWithContent.length > 0 && addHighlight)) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedTextPreserved = selectedText.replace(/^\s+|\s+$/g, '');

    const pdfPage = range.commonAncestorContainer.closest?.('[data-page-number]');
    let pageNumber = currentPage;
    if (pdfPage) {
      const pageNumAttr = pdfPage.getAttribute('data-page-number');
      if (pageNumAttr) {
        pageNumber = parseInt(pageNumAttr, 10);
      }
    }

    const { contextBefore, contextAfter } = extractHighlightContext(range);
    const contextString = buildHighlightContextString(selectedTextPreserved, contextBefore, contextAfter);

    const result = await addHighlight(selectedTextPreserved, {
      page_number: pageNumber,
      section_name: contextString,
      color: 'yellow'
    });

    if (result) {
      toast.success('Text highlighted!');
    }

    clearTextSelection('highlight');
  }, [selectedText, addHighlight, currentPage, clearTextSelection]);

  return {
    handleAskBudleeAIClick,
    handleExplainClick,
    handleReadAloudClick,
    handleHighlightClick,
  };
}
